// A centralized place to import, validate and export environment variables

// TypeScript may not recognize import.meta.env without specific Vite client types.
// Ensure "vite/client" is in your tsconfig.json compilerOptions.types for proper type checking.
// As a workaround for this file, we cast it.
// const viteEnv = import.meta.env as Record<string, string | undefined>;

const LOG_ENV_VARS_REPORT = true;

// Environment Detection
export type Environment = "LOCAL" | "DEV" | "QA" | "STAG" | "PROD";

export function detectEnvironment(): Environment {
  const href = window.location.href;

  if (href.includes("gatewaygxm.hsp.com")) {
    return "PROD";
  }
  if (href.includes("gxm-staging.hsp.com")) {
    return "STAG";
  }
  if (href.includes("gxm-qa.hsp.com")) {
    return "QA";
  }
  if (href.includes("zealous-ground") || href.includes("gxm-dev.hsp.com")) {
    return "DEV";
  }

  return "LOCAL";
}

export const CURRENT_ENVIRONMENT: Environment = detectEnvironment();

interface EnvironmentDefaults {
  local: string;
  develop: string;
  qa: string;
  staging: string;
  production: string;
}

const envKeyMap: Record<Environment, keyof EnvironmentDefaults> = {
  LOCAL: "local",
  DEV: "develop",
  QA: "qa",
  STAG: "staging",
  PROD: "production",
};

// APIM Gateway Base URLs
export const STAGING_APIM_BASE_URL = "https://gxm-stag-apim1.azure-api.net";
export const PROD_APIM_BASE_URL = "https://gxm-prod-apim1.azure-api.net";
export const QA_APIM_BASE_URL = "https://gxm-qa-apim1.azure-api.net";
export const LOCAL_APIM_BASE_URL = "http://localhost:3001";

// APIM Gateway Configuration
export const APIM_GATEWAY_URLS: Record<Environment, string> = {
  LOCAL: `${LOCAL_APIM_BASE_URL}/api`,
  DEV: `${STAGING_APIM_BASE_URL}/develop-functions`,
  QA: `${QA_APIM_BASE_URL}/qa-functions`,
  STAG: `${STAGING_APIM_BASE_URL}/staging-functions`,
  PROD: `${PROD_APIM_BASE_URL}/production-functions`,
} as const;

interface EnvConfig {
  DATADOG_APP_ID: string;
  DATADOG_CLIENT_TOKEN: string;
  ENVIRONMENT: string;
  DEV_CYCLE_SDK_KEY: string;
  WORKOS_LOGIN_URL: string;
  WORKOS_CLIENT_ID: string;
  WORK_OS_API_HOST: string;
  LOCAL_FUNCTIONS_PORT: string;
  NOVU_APP_IDENTIFIER: string;
  APIM_GATEWAY_SUBSCRIPTION_KEY: string;
  BLOB_STORAGE_ACCOUNT: string;
  BLOB_TEMPORARY_CONTAINER: string;
}

const envVarDefinitions: Array<{
  key: keyof EnvConfig;
  default: EnvironmentDefaults;
  description: string;
}> = [
  {
    key: "DATADOG_APP_ID",
    default: {
      // will work only on production
      local: "7f9016bb-6853-409e-9f5f-a9831493e55d",
      develop: "7f9016bb-6853-409e-9f5f-a9831493e55d",
      qa: "7f9016bb-6853-409e-9f5f-a9831493e55d",
      staging: "7f9016bb-6853-409e-9f5f-a9831493e55d",
      production: "7f9016bb-6853-409e-9f5f-a9831493e55d",
    },
    description: "Datadog Application ID",
  },
  {
    key: "DATADOG_CLIENT_TOKEN",
    default: {
      // will work only on production
      local: "pubba3041ccebcc86fb29d46adf779e9e08",
      develop: "pubba3041ccebcc86fb29d46adf779e9e08",
      qa: "pubba3041ccebcc86fb29d46adf779e9e08",
      staging: "pubba3041ccebcc86fb29d46adf779e9e08",
      production: "pubba3041ccebcc86fb29d46adf779e9e08",
    },
    description: "Datadog Client Token",
  },
  {
    key: "ENVIRONMENT",
    default: {
      local: "development",
      develop: "development",
      qa: "development",
      staging: "staging",
      production: "production",
    },
    description: "Application environment (development, staging, production)",
  },
  {
    key: "DEV_CYCLE_SDK_KEY",
    default: {
      local: "dvc_client_535a1a44_cc6e_464c_9559_7314b0a4870d_7926e75",
      develop: "dvc_client_535a1a44_cc6e_464c_9559_7314b0a4870d_7926e75",
      qa: "dvc_client_535a1a44_cc6e_464c_9559_7314b0a4870d_7926e75",
      staging: "dvc_client_e25b3a70_fe41_48ab_8efb_c1ee685345bf_48cdc2a",
      production: "dvc_client_af824949_f4d9_4318_8315_598feb5c13c0_9a8b109",
    },
    description: "DevCycle SDK Key (Different key for different environments)",
  },
  {
    key: "WORKOS_LOGIN_URL",
    default: {
      local: "https://supportive-avenue-54-staging.authkit.app/",
      develop: "https://supportive-avenue-54-staging.authkit.app/",
      qa: "https://supportive-avenue-54-staging.authkit.app/",
      staging: "https://supportive-avenue-54-staging.authkit.app/",
      production: "https://youthful-earth-05.authkit.app/",
    },
    description:
      "WorkOS AuthKit Login URL. Staging: https://supportive-avenue-54-staging.authkit.app/ (for local, dev, qa, staging). Production: https://youthful-earth-05.authkit.app/ (only for production).",
  },
  {
    key: "WORKOS_CLIENT_ID",
    default: {
      local: "client_01J4P6ECFBBG7Z88AKD4GQ5GMR",
      develop: "client_01J4P6ECFBBG7Z88AKD4GQ5GMR",
      qa: "client_01J4P6ECFBBG7Z88AKD4GQ5GMR",
      staging: "client_01J4P6ECFBBG7Z88AKD4GQ5GMR",
      production: "client_01J4P6ECKV2TGF73JH9X3RGK88",
    },
    description:
      "WorkOS Client ID. Staging: client_01J4P6ECFBBG7Z88AKD4GQ5GMR (for local, dev, qa, staging). Production: client_01J4P6ECKV2TGF73JH9X3RGK88 (only for production).",
  },
  {
    key: "WORK_OS_API_HOST",
    default: {
      local: "api.workos.com",
      develop: "api.workos.com",
      qa: "api.workos.com",
      staging: "api.workos.com",
      production: "api.workos.com",
    },
    description: "WorkOS API Host Name. All environments: api.workos.com.",
  },
  {
    key: "LOCAL_FUNCTIONS_PORT",
    default: {
      local: "7071",
      develop: "7071",
      qa: "7071",
      staging: "7071",
      production: "7071",
    },
    description: "Port for local Azure Functions emulator",
  },
  {
    key: "NOVU_APP_IDENTIFIER",
    default: {
      local: "C8p8iAqchvwx",
      develop: "C8p8iAqchvwx",
      qa: "C8p8iAqchvwx",
      staging: "C8p8iAqchvwx",
      production: "hDAyArSJWOVN",
    },
    description: "Novu Application Identifier",
  },
  {
    key: "APIM_GATEWAY_SUBSCRIPTION_KEY",
    default: {
      local: "01ef9b2a8ab84578a7aa9bdcd23c7bb5",
      develop: "01ef9b2a8ab84578a7aa9bdcd23c7bb5",
      qa: "01ef9b2a8ab84578a7aa9bdcd23c7bb5",
      staging: "01ef9b2a8ab84578a7aa9bdcd23c7bb5",
      production: "4381cd25178d4c99b57650d3b85a91a6", // all access. spec access: 30164c509a4246f2abc4e3066fac2271
    },
    description: "APIM Gateway Subscription Key",
  },
  {
    key: "BLOB_STORAGE_ACCOUNT",
    default: {
      local: "gxmstagfnstg",
      develop: "gxmstagfnstg",
      qa: "gxmstagfnstg",
      staging: "gxmstagfnstg",
      production: "gxmprodfuncstorageacct",
    },
    description: "BLOB Storage Account",
  },
  {
    key: "BLOB_TEMPORARY_CONTAINER",
    default: {
      local: "document-repo",
      develop: "document-repo",
      qa: "document-repo",
      staging: "document-repo",
      production: "document-repo",
    },
    description: "BLOB Storage Temporary Container",
  },
];

const AppConfigInProgress: Partial<EnvConfig> = {};

console.info(
  "[Config] Initializing environment configuration from script defaults...",
);
const reportEntries: string[] = [];
const currentEnvKey = envKeyMap[CURRENT_ENVIRONMENT];

for (const { key, default: defaultValues, description } of envVarDefinitions) {
  const defaultValue = defaultValues[currentEnvKey];
  AppConfigInProgress[key] = defaultValue;

  // Truncate long default values for display in the detailed report
  const displayDefault =
    defaultValue.length > 50
      ? `${defaultValue.substring(0, 47)}...`
      : defaultValue;
  const statusMessage = `  - ${key}: LOADED from internal config. Value: "${displayDefault}". (${description})`;
  reportEntries.push(statusMessage);
}

let finalReport = "[Config] Environment configuration processing complete.\n";

finalReport +=
  "\nINFO: All configuration values are loaded directly from the internal script defaults, ignoring system environment variables.\n";
finalReport += "\nFull Configuration Details:\n";

finalReport += reportEntries.join("\n");

if (LOG_ENV_VARS_REPORT) {
  console.info(finalReport);
}
console.info(
  `[Config] Environment configuration loaded. Current environment: ${CURRENT_ENVIRONMENT}`,
);

// Perform a final cast, assuming all keys are now populated (either by env or default)
const Config: EnvConfig = AppConfigInProgress as EnvConfig;

export default Config;
